/**
 * BookingActionsMenu — menu premium « … »
 *
 * Mobile (< 640px) : bottom-sheet modal plein écran
 *   → centrage visuel parfait, pas de coupure possible, expérience premium
 *     type iOS Action Sheet / Linear quick menu.
 * Desktop (≥ 640px) : popover positionné sous le bouton (viewport-aware).
 *
 * Les items de menu ont un relief premium : background soft + ombres,
 * hover plus riche, active:scale-[0.99], ring focus, transitions douces.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MoreHorizontal, X } from 'lucide-react';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const h = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return isMobile;
}

export default function BookingActionsMenu({ actions = [], testId }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(true);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const isMobile = useIsMobile();

  // Close on outside click (desktop) / escape key
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    // Lock body scroll while mobile sheet is open
    if (isMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousedown', onClick);
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, isMobile]);

  // Desktop: viewport-aware popover alignment
  useLayoutEffect(() => {
    if (!open || isMobile || !popoverRef.current || !triggerRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const w = popoverRef.current.offsetWidth || 280;
    const m = 12;
    const overflowsLeft = t.right - w < m;
    const overflowsRight = t.left + w > window.innerWidth - m;
    setAlignRight(!(overflowsLeft && !overflowsRight));
  }, [open, isMobile]);

  const visible = actions.filter(Boolean);
  if (visible.length === 0) return null;

  const renderItems = () =>
    visible.map((a, i) => {
      const Icon = a.icon;
      const destructive = a.destructive;
      return (
        <button
          key={i}
          data-testid={a.testId}
          disabled={a.disabled}
          onClick={() => { setOpen(false); a.onClick?.(); }}
          role="menuitem"
          className={`group relative w-full flex items-start gap-3 px-4 py-3.5 text-left rounded-xl
                      transition-all duration-150 ease-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6D28D9]/40
                      ${a.disabled
                        ? 'opacity-40 cursor-not-allowed'
                        : destructive
                        ? 'hover:bg-red-50 hover:shadow-[inset_0_0_0_1px_#FECACA] active:scale-[0.99] text-[#991B1B]'
                        : 'hover:bg-[#F5F3FF] hover:shadow-[inset_0_0_0_1px_#DDD6FE] active:scale-[0.99] text-[#1C1147]'}`}
        >
          {Icon && (
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-150 group-hover:scale-105
                          ${destructive
                            ? 'bg-gradient-to-br from-[#FEE2E2] to-[#FECACA] shadow-[0_1px_3px_rgba(220,38,38,0.15)]'
                            : 'bg-gradient-to-br from-[#F5F3FF] to-[#EDE9FE] shadow-[0_1px_3px_rgba(109,40,217,0.12)]'}`}
            >
              <Icon
                className={`w-[18px] h-[18px] ${destructive ? 'text-[#DC2626]' : 'text-[#6D28D9]'}`}
                strokeWidth={2.2}
              />
            </div>
          )}
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="text-sm font-semibold leading-tight">{a.label}</div>
            {a.hint && (
              <div className="text-[11.5px] text-[#6B7280] mt-1 leading-snug">{a.hint}</div>
            )}
          </div>
        </button>
      );
    });

  return (
    <>
      <div className="relative" ref={triggerRef}>
        <button
          data-testid={testId || 'booking-actions-trigger'}
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Actions"
          className="w-10 h-10 rounded-full flex items-center justify-center
                     bg-white text-[#6D28D9]
                     border border-[#E8E0F2]
                     shadow-[0_1px_3px_rgba(28,17,71,0.06),0_2px_8px_rgba(28,17,71,0.04)]
                     hover:bg-[#F5F3FF] hover:border-[#C4B5FD]
                     hover:shadow-[0_2px_6px_rgba(109,40,217,0.12),0_4px_16px_rgba(109,40,217,0.08)]
                     active:scale-95
                     transition-all duration-150 ease-out"
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>

        {/* Desktop popover */}
        {open && !isMobile && (
          <div
            ref={popoverRef}
            role="menu"
            data-testid={`${testId || 'booking-actions'}-menu`}
            className={`absolute top-12 z-30 w-[300px] p-1.5 bg-white rounded-2xl border border-[#E8E0F2]
                        animate-in fade-in slide-in-from-top-2 duration-150
                        ${alignRight ? 'right-0' : 'left-0'}`}
            style={{ boxShadow: '0 20px 40px -8px rgba(28,17,71,0.22), 0 2px 6px rgba(28,17,71,0.08)' }}
          >
            {renderItems()}
          </div>
        )}
      </div>

      {/* Mobile bottom-sheet */}
      {open && isMobile && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
          />
          {/* Sheet */}
          <div
            ref={popoverRef}
            data-testid={`${testId || 'booking-actions'}-menu`}
            className="absolute left-2 right-2 bg-white rounded-3xl p-2 animate-in slide-in-from-bottom-4 fade-in duration-200"
            style={{
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              boxShadow: '0 -8px 40px rgba(28,17,71,0.18), 0 2px 6px rgba(28,17,71,0.06)',
            }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-[#E8E0F2]" />
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2">
              <div className="text-[11px] uppercase tracking-widest text-[#6D28D9] font-bold">
                Actions
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-gray-100 active:scale-95 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-1 pb-2">{renderItems()}</div>
          </div>
        </div>
      )}
    </>
  );
}
